import { Link } from 'react-router-dom';

export default function TeamBuildingPage() {
  const sections = [
    {
      number: 'Ⅰ',
      title: '中核概念',
      description: 'チーム成果は「個人能力の総和」ではなく相互作用の質で決まる',
      icon: 'ri-lightbulb-line',
      link: '/team-building/core-concepts',
      color: 'from-rose-500 to-pink-500'
    },
    {
      number: 'Ⅱ',
      title: '四大要素',
      description: '役割の明確化・目標設定・対人関係・問題解決',
      icon: 'ri-stack-line',
      link: '/team-building/four-elements',
      color: 'from-pink-500 to-rose-400'
    },
    {
      number: 'Ⅲ',
      title: '心理的安全性',
      description: '四大要素すべてを機能させる"土壌"',
      icon: 'ri-shield-check-line',
      link: '/team-building/psychological-safety',
      color: 'from-rose-400 to-pink-400'
    },
    {
      number: 'Ⅳ',
      title: 'リーダーの役割',
      description: 'タスク管理者から相互作用の設計者へ',
      icon: 'ri-user-star-line',
      link: '/team-building/leader-role',
      color: 'from-pink-400 to-rose-300'
    },
    {
      number: 'Ⅴ',
      title: '成果最大化の条件',
      description: 'チーム規模・能動的学習・継続設計・デブリーフィング',
      icon: 'ri-line-chart-line',
      link: '/team-building/success-factors',
      color: 'from-rose-300 to-pink-300'
    },
    {
      number: 'Ⅵ',
      title: 'よくある誤解・失敗パターン',
      description: '人間関係改善から始める・研修だけで解決しようとする',
      icon: 'ri-alert-line',
      link: '/team-building/success-factors',
      color: 'from-pink-300 to-rose-200'
    },
    {
      number: 'Ⅶ',
      title: '薬局マネジメントへの翻訳',
      description: '朝礼・1on1・ヒヤリハット・在宅連携・属人業務',
      icon: 'ri-hospital-line',
      link: '/team-building/success-factors',
      color: 'from-rose-200 to-pink-200'
    }
  ];

  const keyPoints = [
    {
      icon: 'ri-settings-3-line',
      title: '構造設計',
      description: '人間関係改善ではなく、構造設計'
    },
    {
      icon: 'ri-team-line',
      title: '相互作用',
      description: '個人能力の総和ではなく相互作用の質'
    },
    {
      icon: 'ri-refresh-line',
      title: '継続的成果',
      description: 'チームが継続的に成果を出せる状態をつくる'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/50 via-pink-100/30 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-team-line text-3xl text-white"></i>
            </div>
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              チームビルディング
            </h1>
            <p className="text-xl text-gray-700 mb-4 leading-relaxed">
              全体構造リスト（保存版）
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              チーム成果は「個人能力の総和」ではなく相互作用の質で決まる。<br />
              チームビルディング＝人間関係改善ではなく、構造設計。
            </p>
          </div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-12 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="relative bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 rounded-2xl p-1 shadow-xl">
              <div className="bg-white rounded-xl p-8">
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                  {keyPoints.map((point, index) => (
                    <div key={index} className="flex items-center gap-4 md:gap-6">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-md">
                          <i className={`${point.icon} text-xl text-white`}></i>
                        </div>
                        <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                          {point.title}
                        </span>
                      </div>
                      {index < keyPoints.length - 1 && (
                        <span className="text-3xl md:text-4xl font-bold text-orange-400">×</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-gray-600 mt-6 text-lg">
                  チームビルディングの3つの核心
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sections Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sections.map((section, index) => (
                <Link
                  key={index}
                  to={section.link}
                  className="group bg-white rounded-2xl p-8 shadow-sm border border-rose-100 hover:shadow-xl hover:border-rose-300 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`flex items-center justify-center w-14 h-14 bg-gradient-to-br ${section.color} rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${section.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-2xl font-bold text-rose-600 mb-1">{section.number}</div>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-rose-600 transition-colors duration-300">
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {section.description}
                  </p>
                  <div className="flex items-center text-rose-600 font-medium text-sm group-hover:gap-2 transition-all duration-300">
                    <span>詳しく見る</span>
                    <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform duration-300"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-rose-500 to-pink-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-6">チームビルディングの目的</h2>
            <p className="text-xl leading-relaxed mb-8">
              チームが継続的に成果を出せる状態をつくること
            </p>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
              <i className="ri-information-line text-xl"></i>
              <span className="font-medium">各セクションで詳細な実践方法を解説しています</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
